import React, { useEffect, useState } from "react"
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Spinner from "../ui/Spinner"
import useT from "../hooks/useT"
import useSearchParams from "../hooks/useSearchParams"
import { usePageTitle } from "../hooks/usePageTitle"
import { TouchLicenseCard } from "../api"

// license-card {touch} answer → [title key, message key]
const STATES = {
  "mobile-only": ["lc_mobile_only", "lc_mobile_only_message"],
  "no-license": ["lc_no_license", "lc_no_license_message"],
  pending: ["lc_activate_license", ""],
  congrats: ["lc_congrats", "lc_congrats_message"],
  "not-found": ["lc_no_license", "lc_no_license_message"],
  "not-allowed": ["lc_valid_subscription_required", "lc_valid_subscription_required_message"],
  "already-paired": ["lc_already_activated", "lc_already_activated_message"],
}

/**
 * /license?l=<lcid> (the card's QR) or /license?code=<printed code> — license
 * card activation (old ActivateLicenseCard): on a phone the page touches the
 * card (`license-card {touch}` records the phone's ip) so the Aldoo Kids app
 * on the same network can claim it; desktop browsers only get the hint.
 */
export default function LicensePage() {
  const t = useT()
  const params = useSearchParams()
  const lcid = params.get("l")
  const code = params.get("code") || params.get("c")
  const [state, setState] = useState("pending")
  usePageTitle(t`lc_activate_license`)

  useEffect(() => {
    const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    if (!mobile) return setState("mobile-only")
    if (!lcid && !code) return setState("no-license")
    let cancelled = false
    TouchLicenseCard(lcid ? { lcid } : { code })
      .then((res) => {
        if (cancelled) return
        if (res?.result) return setState("congrats")
        setState(STATES[res?.error] ? res.error : "not-found")
      })
      .catch(() => !cancelled && setState("not-found"))
    return () => {
      cancelled = true
    }
  }, [lcid, code])

  const [title, message] = STATES[state]
  const ok = state === "congrats"
  const waiting = state === "pending"

  return (
    <div className="w-full bg-paper text-text">
      <div className="w-full max-w-[720px] mx-auto px-4 py-20 flex flex-col items-center gap-6 text-center" data-testid="license-page" data-state={state}>
        <img src="/static/p-images/aldoo-small--150x150.webp" alt="Aldoo" width={90} height={90} />
        {waiting && <Spinner />}
        {!waiting && (ok ? <CheckCircleIcon className="w-16 h-16 text-primary" /> : <ExclamationTriangleIcon className="w-16 h-16 text-warning" />)}
        <h1 className="m-0 text-[28px] md:text-[40px] font-bold">
          {t(title)}
        </h1>
        <p className="m-0 max-w-[560px] whitespace-pre-line break-words text-text">
          {t(message)}
        </p>
        {(lcid || code) && (
          <p className="m-0 rounded-lg bg-white/10 px-4 py-3 font-mono text-[14px]">
            {t`License card`}: {code || lcid}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="primary_l" href="/user/add-ons">{t`My account`}</Button>
          <Button variant="secondary_l" href="/help-center">
              {t`Help Center`}
            </Button>
        </div>
      </div>
    </div>
  )
}
